import { useState, useEffect, useContext } from 'react';
import { Modal, ModalOverflow, ModalClose, ModalDialog, DialogTitle, DialogContent, Stack, Box, Avatar, Typography, Input, Button, FormControl, FormLabel, Switch, Textarea, Grid, Chip, CircularProgress, Alert } from '@mui/joy';
import { LayoutContext } from '../../context';
import { APIGetUserProfile, APIUpdateUserProfile, APIChangePassword, APIUploadAvatar, API_URL, APIDeleteAvatar } from '../../api';
import { AnimatePresence, motion } from 'framer-motion';



export default function ModalAccount({ open, onClose }) {
    const { user, token, updateUser } = useContext(LayoutContext);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [avatarRemoved, setAvatarRemoved] = useState(false);
    
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        bio: '',
        date_of_birth: '',
        email_notifications: true,
        show_statistics: true,
    });

    // Password change state
    const [pwOpen, setPwOpen] = useState(false);
    const [pwCurrent, setPwCurrent] = useState('');
    const [pwNew, setPwNew] = useState('');
    const [pwConfirm, setPwConfirm] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');

    useEffect(() => {
        if (open && user) {
            loadUserProfile();
        }
    }, [open, user]);

    const loadUserProfile = async () => {
        setLoading(true);
        try {
            const response = await APIGetUserProfile(token);
            if (response.success) {
                setFormData({
                    first_name: response.user.first_name || '',
                    last_name: response.user.last_name || '',
                    phone: response.user.phone || '',
                    bio: response.user.bio || '',
                    date_of_birth: response.user.date_of_birth || '',
                    email_notifications: response.user.email_notifications,
                    show_statistics: response.user.show_statistics,
                });
                
                // Сбрасываем состояния аватара
                setAvatarFile(null);
                setAvatarPreview(null);
                setAvatarRemoved(false);

                // Устанавливаем превью аватара
                if (response.user.avatar) {
                    setAvatarPreview(`${API_URL}${response.user.avatar}?${Date.now()}`); // Добавляем timestamp для избежания кеширования
                }
            }
        } catch (error) {
            console.error('Error loading profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверка типа файла
        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения (JPEG, PNG, GIF, WEBP)');
            return;
        }

        // Проверка размера файла (максимум 2MB)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('Размер файла слишком большой. Максимальный размер: 2MB');
            return;
        }

        setAvatarFile(file);
        setAvatarRemoved(false); // Сбрасываем флаг удаления если выбрали новый файл
        
        // Создаем превью
        const reader = new FileReader();
        reader.onload = (e) => setAvatarPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveAvatar = async () => {
        try {
            // Если аватар уже загружен на сервер - удаляем его через API
            if (user?.avatar && !avatarFile) {
                const response = await APIDeleteAvatar(token);
                if (response.success) {
                console.log('Аватар удален на сервере');
                }
            }
            
            // Сбрасываем локальные состояния
            setAvatarFile(null);
            setAvatarPreview(null);
            setAvatarRemoved(true);
            
            // Сбрасываем input file
            const fileInput = document.getElementById('avatar-upload');
            if (fileInput) {
                fileInput.value = '';
            }
            
            // Немедленно обновляем пользователя в контексте
            updateUser({ ...user, avatar: null });
            
        } catch (error) {
            console.error('Error removing avatar:', error);
            alert('Ошибка при удалении аватара');
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            // 1. Обрабатываем аватар
            if (avatarRemoved) {
                // Если аватар был удален - убедимся что он удален на сервере
                await APIDeleteAvatar(token);
            } else if (avatarFile) {
                // Если выбран новый файл - загружаем его
                try {
                    const uploadResponse = await APIUploadAvatar(token, avatarFile);
                    if (uploadResponse.success) {
                        console.log('Аватар успешно загружен');
                    }
                } catch (uploadError) {
                    console.error('Error uploading avatar:', uploadError);
                    // Продолжаем обновление профиля даже если аватар не загрузился
                }
            }

            // 2. Обновляем профиль
            const profileData = { ...formData };
            
            // Убедимся, что пустые строки отправляются как null
            if (profileData.phone === '') profileData.phone = null;
            if (profileData.bio === '') profileData.bio = null;
            if (profileData.date_of_birth === '') profileData.date_of_birth = null;

            const response = await APIUpdateUserProfile(token, profileData);
            if (response.success) {
                // Обновляем данные пользователя с учетом изменений аватара
                const updatedUser = { 
                ...response.user,
                avatar: avatarRemoved ? null : (response.user.avatar || user.avatar)
                };
                updateUser(updatedUser);
                onClose();
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            alert('Ошибка при сохранении профиля');
        } finally {
            setSaving(false);
        }
    };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwError('Заполни все поля');
      return;
    }
    if (pwNew.length < 8) {
      setPwError('Новый пароль должен быть от 8 символов');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('Новый пароль и подтверждение не совпадают');
      return;
    }
    setPwSaving(true);
    try {
      const res = await APIChangePassword(token, {
        old_password: pwCurrent,
        new_password: pwNew,
      });
      if (res?.success) {
        setPwSuccess('Пароль успешно изменён');
        setPwCurrent(''); setPwNew(''); setPwConfirm('');
        setTimeout(() => { setPwOpen(false); setPwSuccess(''); }, 1500);
      } else {
        setPwError(res?.error || 'Не удалось сменить пароль');
      }
    } catch (e) {
      const srvErr = e?.response?.data;
      const msg = srvErr?.error
        || (srvErr?.details ? Object.values(srvErr.details).flat().join(' ') : null)
        || e.message
        || 'Ошибка';
      setPwError(msg);
    } finally {
      setPwSaving(false);
    }
  };

  // Определяем, есть ли текущий аватар (уже загруженный на сервер)
  const hasExistingAvatar = user?.avatar && !avatarFile && !avatarRemoved;

//   if (loading) {
//     return (
//       <Modal open={open} onClose={onClose}>
//         <ModalDialog>
//           <Typography>Загрузка профиля...</Typography>
//         </ModalDialog>
//       </Modal>
//     );
//   }

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    open={open}
                    // onClose={onClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow>
                        {/* { loading ?
                            <ModalDialog>
                                <Typography>Загрузка профиля...</Typography>
                            </ModalDialog>
                            : */}
                            <ModalDialog
                                component={motion.div}
                                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                sx={{
                                    width: '90%',
                                    maxWidth: 500,
                                    maxHeight: '90vh',
                                    overflow: 'auto'
                                }}
                            >
                                <ModalClose onClick={onClose} />

                                <DialogTitle>Профиль пользователя</DialogTitle>
                                
                                <DialogContent>
                                    <Stack spacing={3}>
                                    <Grid container spacing={2}>
                                        <Grid xs={12} md={4}>
                                        <Stack alignItems="center" spacing={2}>
                                            <Avatar 
                                                color='primary'
                                                src={avatarPreview} 
                                                sx={{ width: 100, height: 100 }}
                                            />
                                            <input
                                                type="file"
                                                id="avatar-upload"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={handleAvatarChange}
                                            />
                                            <Button 
                                            variant="outlined" 
                                            size="sm"
                                            onClick={() => document.getElementById('avatar-upload').click()}
                                            >
                                            Сменить аватар
                                            </Button>
                                            {avatarPreview && (
                                            <Button 
                                                variant="plain" 
                                                size="sm" 
                                                color="danger"
                                                onClick={handleRemoveAvatar}
                                            >
                                                Удалить
                                            </Button>
                                            )}
                                        </Stack>
                                        </Grid>
                                        <Grid xs={12} md={8}>
                                        <Stack spacing={2}>
                                            <FormControl>
                                            <FormLabel>Имя</FormLabel>
                                            <Input
                                                value={formData.first_name}
                                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                            />
                                            </FormControl>
                                            
                                            <FormControl>
                                            <FormLabel>Фамилия</FormLabel>
                                            <Input
                                                value={formData.last_name}
                                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                            />
                                            </FormControl>
                                            
                                            <FormControl>
                                            <FormLabel>Телефон</FormLabel>
                                            <Input
                                                value={formData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                placeholder="+7 (XXX) XXX-XX-XX"
                                            />
                                            </FormControl>
                                            
                                            <FormControl>
                                            <FormLabel>Дата рождения</FormLabel>
                                            <Input
                                                type="date"
                                                value={formData.date_of_birth}
                                                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                                            />
                                            </FormControl>
                                        </Stack>
                                        </Grid>
                                    </Grid>

                                    <FormControl>
                                        <FormLabel>О себе</FormLabel>
                                        <Textarea
                                        minRows={3}
                                        value={formData.bio}
                                        onChange={(e) => handleInputChange('bio', e.target.value)}
                                        placeholder="Расскажите о себе..."
                                        />
                                    </FormControl>

                                    {/* <Stack spacing={2}>
                                        <Switch
                                        label="Email уведомления"
                                        checked={formData.email_notifications}
                                        onChange={(e) => handleInputChange('email_notifications', e.target.checked)}
                                        />
                                        
                                        <Switch
                                        label="Показывать статистику публично"
                                        checked={formData.show_statistics}
                                        onChange={(e) => handleInputChange('show_statistics', e.target.checked)}
                                        />
                                    </Stack> */}

                                    <Button
                                        loading={saving}
                                        onClick={handleSaveProfile}
                                        size="lg"
                                    >
                                        Сохранить изменения
                                    </Button>

                                    {/* Смена пароля — collapse-секция, открывается по клику */}
                                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mt: 1 }}>
                                        <Button
                                            variant="plain"
                                            color="neutral"
                                            size="sm"
                                            onClick={() => setPwOpen(o => !o)}
                                            sx={{ justifyContent: 'flex-start', px: 0, fontWeight: 500 }}
                                        >
                                            {pwOpen ? '▾ Сменить пароль' : '▸ Сменить пароль'}
                                        </Button>
                                        {pwOpen && (
                                            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                                                <FormControl>
                                                    <FormLabel>Текущий пароль</FormLabel>
                                                    <Input
                                                        type="password"
                                                        value={pwCurrent}
                                                        onChange={(e) => setPwCurrent(e.target.value)}
                                                        autoComplete="current-password"
                                                    />
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel>Новый пароль</FormLabel>
                                                    <Input
                                                        type="password"
                                                        value={pwNew}
                                                        onChange={(e) => setPwNew(e.target.value)}
                                                        autoComplete="new-password"
                                                        placeholder="Минимум 8 символов"
                                                    />
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel>Повторите новый пароль</FormLabel>
                                                    <Input
                                                        type="password"
                                                        value={pwConfirm}
                                                        onChange={(e) => setPwConfirm(e.target.value)}
                                                        autoComplete="new-password"
                                                    />
                                                </FormControl>
                                                {pwError && <Alert color="danger" size="sm">{pwError}</Alert>}
                                                {pwSuccess && <Alert color="success" size="sm">{pwSuccess}</Alert>}
                                                <Button
                                                    onClick={handleChangePassword}
                                                    loading={pwSaving}
                                                    variant="outlined"
                                                    sx={{ alignSelf: 'flex-start' }}
                                                >
                                                    Сменить пароль
                                                </Button>
                                            </Stack>
                                        )}
                                    </Box>
                                    </Stack>
                                </DialogContent>
                            </ModalDialog>
                        {/* } */}
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}